import type { HomeStats } from '@aj/core';
import { Card, HStack, Text, VStack } from '@aj/ui';
import type { ReactNode } from 'react';

import { useI18n, useT } from '../../providers/I18nProvider.js';

/**
 * The numbers around the wall chart (§53.6).
 *
 * This is the part of a habit tracker people screenshot, so it has to be good
 * — but the composition is an argument, not decoration:
 *
 *  • **Kept-rate leads.** It is a fact about the person that a bad day cannot
 *    destroy, and it is the number the app wants someone to identify with.
 *  • **The streak is here and is not the hero.** §45 names "the biggest number
 *    on screen is a streak" as the tell of the scoreboard mindset. It is shown
 *    at the same weight as everything else, always beside its best-ever, so a
 *    broken streak reads as a fact rather than a loss.
 *  • **Comebacks sit next to the streak on purpose.** The pair says the two
 *    halves of §45's sentence: I keep my word, and when I break it I come back.
 *  • **Minutes carry their own trend, including downward.** A tracker that
 *    only reports improvement stops being believed the rest of the time.
 *
 * Every tile degrades to a sentence rather than a zero. A zero on day one is a
 * verdict on someone who has not done anything wrong yet.
 */
export type StatBandProps = { stats: HomeStats; className?: string };

export function StatBand({ stats, className }: StatBandProps) {
  const t = useT();
  const i18n = useI18n();
  const { commitment, comebacks, minutes, streak } = stats;

  return (
    <HStack gap={3} align="stretch" className={className}>
      <Tile
        label={t('stats.kept')}
        value={commitment ? t('home.percent', { value: Math.round(commitment.ratio * 100) }) : '—'}
        tone="accent"
        note={
          commitment
            ? t('stats.keptOf', {
                kept: Math.round(commitment.kept),
                promised: Math.round(commitment.promised),
              })
            : t('stats.keptSoon')
        }
      />

      <Tile
        label={t('stats.streak')}
        value={i18n.formatNumber(streak.current)}
        tone="gold"
        note={streak.best > 0 ? t('stats.streakBest', { count: streak.best }) : undefined}
      />

      <Tile
        label={t('stats.comebacks')}
        value={comebacks.gaps === 0 ? '—' : i18n.formatNumber(comebacks.returns)}
        tone="flame"
        note={
          comebacks.gaps === 0
            ? t('stats.comebacksNone')
            : t('stats.comebacksOf', { returns: comebacks.returns, gaps: comebacks.gaps })
        }
      />

      <Tile
        label={t('stats.thisWeek')}
        value={i18n.formatDuration(minutes.minutes)}
        tone="ink"
        note={weekNote(minutes, i18n, t)}
      />
    </HStack>
  );
}

type TileProps = {
  label: string;
  value: ReactNode;
  note?: string | undefined;
  tone: 'accent' | 'gold' | 'flame' | 'ink';
};

function Tile({ label, value, note, tone }: TileProps) {
  return (
    <Card padding={4} className="min-w-0 flex-1">
      <VStack gap={1}>
        <Text variant="overline" tone="subtle" className="truncate">
          {label}
        </Text>
        <Text variant="display" tone={tone} numeric className="text-xl">
          {value}
        </Text>
        {/* Always a sentence, never a bare number needing interpretation. */}
        <Text variant="caption" tone="subtle" className="truncate">
          {note ?? ' '}
        </Text>
      </VStack>
    </Card>
  );
}

function weekNote(
  minutes: HomeStats['minutes'],
  i18n: ReturnType<typeof useI18n>,
  t: ReturnType<typeof useT>,
): string {
  if (minutes.ratio === null) return t('stats.firstWeek');
  if (minutes.delta === 0) return t('stats.sameAsLastWeek');

  // The sign is spelled out rather than left to a coloured arrow: a fall and a
  // rise must be distinguishable without colour, and "+2h 30m" says which.
  const sign = minutes.delta > 0 ? '+' : '−';
  return t('stats.vsLastWeek', {
    delta: `${sign}${i18n.formatDuration(Math.abs(minutes.delta))}`,
  });
}
